import React, { useState } from "react";
import moment from "moment";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import { useManifestDetails } from "../hooks/useManifestDetails";
import { useManifestNotes, manifestNoteTypeOptions, ManifestNote } from "../hooks/useManifestNotes";
import { ReactComponent as AlertIcon } from "../../../images/alert.svg";
import { ReactComponent as ArrowBlueRight } from "../../../images/arrow_blue_right.svg";
import Button, { ButtonThemes } from "ui-components/button/button";
import XGSSelect, { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { useSelector } from "react-redux";
import { userSelector } from "slices/user/userSlice";
import { toast } from "react-toastify";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";

import "./manifestDetailsSlideIn.scss";

const formatStatusLabel = (value?: string) => {
  if (!value) return "Active";
  return value
    .toString()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getStatusColor = (status: string) => {
  if (status === "ARRIVED" || status === "ACTIVE") return TagColor.GREEN;
  if (status === "UNLOADING") return TagColor.PURPLE;
  if (status === "LATE" || status === "ALERT") return TagColor.RED;
  if (status === "INBOUND" || status === "IN_TRANSIT") return TagColor.BLUE;
  if (status === "UNLOADED" || status === "STAGED") return TagColor.GREY;
  return TagColor.GREEN;
};

export interface ManifestDetailsSlideInProps {
  isOpen: boolean;
  onClose: () => void;
  freight?: any;
  destinationTerminalName?: string;
}

const pick = (api: any, freight: any, ...keys: string[]) => {
  for (const key of keys) {
    const v = api?.[key] ?? freight?.[key];
    if (v != null && v !== "") return v;
  }
  return undefined;
};

/** Parse delay hours from lastCheckCallMessage1 e.g. "Arrived in                333 Hrs Late" */
const parseDelayHours = (msg?: string): number | null => {
  if (!msg || typeof msg !== "string") return null;
  const m = msg.match(/(\d+)\s*hrs?\s*late/i);
  return m ? parseInt(m[1], 10) : null;
};

/** Format rate type: "MI" -> "Per Mile (MI)" */
const formatRateType = (rt?: string) => {
  if (!rt) return "-";
  if (rt === "MI") return "Per Mile (MI)";
  return rt;
};

/** Format team: "Y"/"N" -> "Yes"/"No" */
const formatTeam = (t?: string) => (t === "Y" ? "Yes" : t === "N" ? "No" : t ?? "-");

/** Abbreviate division name for Service Program display to match Figma */
const formatServiceProgramDisplay = (name?: string): string => {
  if (!name || !name.trim()) return "-";
  const n = name.trim().toUpperCase();
  if (n.includes("XPRESS GLOBAL") || n.includes("XPRESS GL") || n.startsWith("XPRESS")) {
    return "XPRESS GL";
  }
  return name;
};

const ManifestDetailsSlideIn: React.FC<ManifestDetailsSlideInProps> = ({
  isOpen,
  onClose,
  freight,
  destinationTerminalName,
}) => {
  const userState = useSelector(userSelector);
  const manifestNumber = freight?.manifestNumber;
  const { data: apiData, isLoading, error } = useManifestDetails(
    isOpen && manifestNumber ? String(manifestNumber) : null
  );
  const {
    addNote,
    addNoteLoading,
    updateNote,
    updateNoteLoading,
    deleteNote,
    deleteNoteLoading,
  } = useManifestNotes(isOpen && manifestNumber ? manifestNumber : null);

  const notes = apiData?.notes ?? [];

  const [noteText, setNoteText] = useState("");
  const [selectedNoteType, setSelectedNoteType] = useState<XGSSelectOption | null>(null);
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [deleteModalNoteId, setDeleteModalNoteId] = useState<string | number | null>(null);

  const formatDateTime = (val: any) => {
    if (!val) return "-";
    const m = moment(val);
    return m.isValid() ? m.format("MMM D, YYYY h:mm A") : "-";
  };

  const segments = apiData?.segments ?? [];
  const originSegment = segments.find((s: any) => s.tp === "OR");
  const destSegment = segments.find((s: any) => s.tp === "DS") ?? segments[segments.length - 1];

  // Summary badge: manifest details API response status (e.g. Active)
  const status = pick(apiData, freight, "status", "freightStatus") ?? "-";
  // Destination Status: freight status from list (e.g. In Transit, Unloading, Arrived)
  const destinationStatus = pick(freight, apiData, "freightStatus", "status") ?? "-";
  const tractorNumber =
    pick(apiData, freight, "tractor", "tractorNumber") ?? freight?.tractor ?? "-";
  const trailerRaw = pick(apiData, freight, "trailer", "trailerNumber");
  const trailerNumber = trailerRaw
    ? (() => {
        const trailerStr = String(trailerRaw).toUpperCase();
        return trailerStr.startsWith("TRL-") ? trailerStr : `TRL-${trailerStr}`;
      })()
    : "-";
  const miles = apiData?.miles ?? freight?.miles ?? freight?.distance;
  const distance = miles != null ? `${miles} mi` : "-";
  const delayHours =
    parseDelayHours(apiData?.lastCheckCallMessage1) ??
    pick(apiData, freight, "delayHours");
  const hasDelay = delayHours != null && Number(delayHours) > 0;

  const originTerminal =
    originSegment?.terminalName ??
    originSegment?.airCode ??
    pick(apiData, freight, "originCity", "originTerminalName", "originAirportCode", "originTerminal", "origin") ??
    "-";
  const destTerminalName = destSegment?.terminalName ?? destSegment?.airCode;
  const destTermNum = destSegment?.terminalNumber;
  const destinationTerminal = (() => {
    if (destTerminalName == null) {
      return pick(apiData, freight, "destinationTerminalName") ?? destinationTerminalName ?? "-";
    }
    const airCode = destSegment?.airCode;
    const name = destSegment?.terminalName;
    if (name && airCode && name !== airCode) {
      return destTermNum != null ? `${airCode} - ${name} ${destTermNum}` : `${airCode} - ${name}`;
    }
    if (name) {
      return destTermNum != null ? `${name} ${destTermNum}` : name;
    }
    return destTermNum != null ? `${destTerminalName} ${destTermNum}` : String(destTerminalName);
  })();

  const departureTime = formatDateTime(
    originSegment?.actDepart ?? originSegment?.estDepart ?? apiData?.dispatchedDateTime ?? freight?.originDate
  ) || "-";
  const actualArrival =
    formatDateTime(
      apiData?.lastCheckCallDateTime ??
        destSegment?.actArrival ??
        freight?.actualArrivalDateTime ??
        freight?.estimatedArrivalDateTime
    ) || "-";
  const expectedArrival =
    formatDateTime(
      destSegment?.estArrival ?? destSegment?.orgEta ?? freight?.estimatedArrivalDateTime
    ) || "-";
  const dispatchedDate =
    formatDateTime(apiData?.dispatchedDateTime ?? freight?.originDate ?? freight?.dispatchedDate) || "-";

  const carrierName = pick(apiData, freight, "carrierName");
  const carrierNumber = pick(apiData, freight, "carrierNumber");
  const carrier =
    carrierName != null && carrierNumber != null && String(carrierNumber) !== ""
      ? `${carrierName}(${carrierNumber})`
      : carrierName ?? (carrierNumber != null ? String(carrierNumber) : null) ?? "-";

  const divisionName = pick(apiData, freight, "divisionName");
  const divisionCode = pick(apiData, freight, "division");
  const division =
    divisionName != null && divisionCode != null && String(divisionCode) !== ""
      ? `${formatServiceProgramDisplay(String(divisionName))}(${divisionCode})`
      : (formatServiceProgramDisplay(String(divisionName ?? "")) || divisionCode) ?? "-";

  const serviceType = pick(apiData, freight, "serviceType") ?? "LineHaul Pro";
  const driverId = pick(apiData, freight, "driverId");
  const driverName = pick(apiData, freight, "driverName", "driver") ?? "-";
  const driverPhone = pick(apiData, freight, "driverPhone", "phoneNumber") ?? "-";
  const trackDriverUrl = driverId != null && driverId !== ""
    ? `https://cloud.samsara.com/o/25236/fleet/drivers?q=${encodeURIComponent(String(driverId))}`
    : null;
  const teamDrivers = formatTeam(pick(apiData, freight, "team", "teamDrivers"));

  const totalCostRaw = pick(apiData, freight, "totalCost");
  const totalCost =
    totalCostRaw != null
      ? typeof totalCostRaw === "number"
        ? `$${Number(totalCostRaw).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
        : String(totalCostRaw)
      : "-";

  const rateType = formatRateType(pick(apiData, freight, "rateType"));
  const rateOverride = pick(apiData, freight, "rateOverride");
  const rateOverrideDisplay =
    rateOverride === "Y" ? "Yes" : rateOverride === "N" ? "No" : rateOverride ?? "-";

  const revenueRaw =
    destSegment?.revenue ??
    (segments.length > 0
      ? segments.reduce((sum: number, s: any) => sum + (Number(s?.revenue) || 0), 0)
      : undefined);
  const revenue =
    revenueRaw != null
      ? `$${Number(revenueRaw).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
      : "-";
  const revenuePercentRaw = pick(apiData, freight, "revenuePercent");
  const revenuePercent =
    revenuePercentRaw != null ? `${Number(revenuePercentRaw).toFixed(2)}%` : "-";

  const manifestNum = manifestNumber != null ? Number(manifestNumber) : 0;
  const authorName =
    userState.profile?.name ||
    [userState.profile?.firstName, userState.profile?.lastName].filter(Boolean).join(" ").trim() ||
    "Unknown";

  const formatNoteDate = (val?: string) => {
    if (!val) return "-";
    const m = moment(val);
    return m.isValid() ? m.format("MMMM D, YYYY h:mm A") : "-";
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !selectedNoteType?.value || !manifestNum) return;
    try {
      await addNote({
        manifestNumber: manifestNum,
        note: noteText.trim(),
        noteType: selectedNoteType.value,
      });
      toast.success("Note added successfully");
      setNoteText("");
      setSelectedNoteType(null);
    } catch (err: any) {
      toast.error(err?.message || "Failed to add note");
    }
  };

  const handleUpdateNote = async (note: ManifestNote) => {
    if (!editingNoteText.trim() || !note.id || !manifestNum) return;
    try {
      await updateNote({
        noteId: note.id,
        payload: {
          manifestNumber: manifestNum,
          note: editingNoteText.trim(),
          noteType: note.noteType,
        },
      });
      toast.success("Note updated successfully");
      setEditingNoteIndex(null);
      setEditingNoteText("");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update note");
    }
  };

  const handleDeleteNote = async () => {
    if (!deleteModalNoteId) return;
    try {
      await deleteNote(deleteModalNoteId);
      toast.success("Note deleted successfully");
      setDeleteModalNoteId(null);
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete note");
    }
  };

  const startEditNote = (note: ManifestNote, index: number) => {
    setEditingNoteIndex(index);
    setEditingNoteText(note.note ?? "");
  };

  return (
    <SlideOutSidebar
      header="Manifest Details"
      onClose={onClose}
      show={isOpen}
      size="medium-860"
    >
      <div className="manifest-details-slide-in">
        {isLoading && (
          <div className="manifest-details-slide-in__skeleton">
            <div className="manifest-details-slide-in__skeleton-summary">
              <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--wide" />
              <div className="manifest-details-slide-in__skeleton-badges">
                <div className="manifest-details-slide-in__skeleton-pill" />
                <div className="manifest-details-slide-in__skeleton-pill manifest-details-slide-in__skeleton-pill--narrow" />
              </div>
              <div className="manifest-details-slide-in__skeleton-separator" />
              <div className="manifest-details-slide-in__skeleton-meta">
                <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--short" />
                <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--short" />
              </div>
            </div>
            <div className="manifest-details-slide-in__skeleton-cards">
              <div className="manifest-details-slide-in__skeleton-card">
                <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--header" />
                <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--label" />
                <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--value" />
                <div className="manifest-details-slide-in__skeleton-row">
                  <div className="manifest-details-slide-in__skeleton-field">
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--label" />
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--value" />
                  </div>
                  <div className="manifest-details-slide-in__skeleton-field">
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--label" />
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--value" />
                  </div>
                </div>
              </div>
              <div className="manifest-details-slide-in__skeleton-card">
                <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--header" />
                <div className="manifest-details-slide-in__skeleton-row manifest-details-slide-in__skeleton-row--route">
                  <div className="manifest-details-slide-in__skeleton-field">
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--label" />
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--value" />
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--small" />
                  </div>
                  <div className="manifest-details-slide-in__skeleton-arrow" />
                  <div className="manifest-details-slide-in__skeleton-field">
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--label" />
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--value" />
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--small" />
                  </div>
                </div>
                <div className="manifest-details-slide-in__skeleton-row">
                  <div className="manifest-details-slide-in__skeleton-field">
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--label" />
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--short" />
                  </div>
                  <div className="manifest-details-slide-in__skeleton-field">
                    <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--label" />
                    <div className="manifest-details-slide-in__skeleton-pill manifest-details-slide-in__skeleton-pill--small" />
                  </div>
                </div>
              </div>
            </div>
            <div className="manifest-details-slide-in__skeleton-card manifest-details-slide-in__skeleton-card--full">
              <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--header" />
              <div className="manifest-details-slide-in__skeleton-row">
                <div className="manifest-details-slide-in__skeleton-field">
                  <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--label" />
                  <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--value" />
                </div>
                <div className="manifest-details-slide-in__skeleton-field">
                  <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--label" />
                  <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--value" />
                </div>
                <div className="manifest-details-slide-in__skeleton-field">
                  <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--label" />
                  <div className="manifest-details-slide-in__skeleton-line manifest-details-slide-in__skeleton-line--value" />
                </div>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="manifest-details-slide-in__error">
            <XGSErrorMessage>Failed to load manifest details. Please try again.</XGSErrorMessage>
          </div>
        )}
        {!isLoading && !error && (
          <>
        {/* Summary: Manifest, Status, Late badge, Tractor, Trailer */}
        <div className="manifest-details-slide-in__summary">
          <div className="manifest-details-slide-in__summary-manifest">
            <span className="manifest-details-slide-in__summary-manifest-label">Manifest:</span>{" "}
            {manifestNumber ?? apiData?.manifestNumber ?? "-"}
          </div>
          <div className="manifest-details-slide-in__summary-right">
            <div className="manifest-details-slide-in__summary-badges">
              <Tag mods={{ color: getStatusColor(status) }}>
                {formatStatusLabel(status)}
              </Tag>
              {hasDelay && (
                <Tag mods={{ color: TagColor.RED }}>
                  <span className="manifest-details-slide-in__delay-icon">
                    <AlertIcon />
                  </span>{" "}
                  {delayHours} hrs late
                </Tag>
              )}
            </div>
            <div className="manifest-details-slide-in__summary-separator" />
            <div className="manifest-details-slide-in__summary-meta">
              <div className="manifest-details-slide-in__summary-meta-item">
                <span className="manifest-details-slide-in__summary-meta-label">Tractor</span>
                <span className="manifest-details-slide-in__summary-meta-value">{tractorNumber}</span>
              </div>
              <div className="manifest-details-slide-in__summary-meta-item">
                <span className="manifest-details-slide-in__summary-meta-label">Trailer</span>
                <span className="manifest-details-slide-in__summary-meta-value">{trailerNumber}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Two-column cards - Current Location and Route Lifecycle in row 1 (same height) */}
        <div className="manifest-details-slide-in__cards-grid">
          {/* Current Location (Last Check Call) */}
          <div className="manifest-details-slide-in__card">
              <div className="manifest-details-slide-in__card-header">Current Location (Last Check Call)</div>
              <div className="manifest-details-slide-in__field-stack">
                <div className="manifest-details-slide-in__field">
                  <div className="manifest-details-slide-in__field-label">Terminal</div>
                  <div className="manifest-details-slide-in__field-value">{destinationTerminal}</div>
                </div>
              </div>
              <div className="manifest-details-slide-in__field-row">
                <div className="manifest-details-slide-in__field">
                  <div className="manifest-details-slide-in__field-label">Actual Arrival</div>
                  <div className="manifest-details-slide-in__field-value">{actualArrival}</div>
                </div>
                <div className="manifest-details-slide-in__field">
                  <div className="manifest-details-slide-in__field-label">Expected Arrival</div>
                  <div className="manifest-details-slide-in__field-value">{expectedArrival}</div>
                </div>
              </div>
              {hasDelay && (
                <div className="manifest-details-slide-in__alert-banner">
                  <span className="manifest-details-slide-in__alert-banner-icon">
                    <AlertIcon />
                  </span>
                  {delayHours} hrs late - Immediate attention required
                </div>
              )}
            </div>

          {/* Route Lifecycle */}
          <div className="manifest-details-slide-in__card">
              <div className="manifest-details-slide-in__card-header">Route Lifecycle</div>
              <div className="manifest-details-slide-in__route-lifecycle">
                <div className="manifest-details-slide-in__route-point">
                  <div className="manifest-details-slide-in__route-point-label">Origin</div>
                  <div className="manifest-details-slide-in__route-point-value">{originTerminal}</div>
                  <div className="manifest-details-slide-in__route-point-time">
                    Dep: {departureTime}
                  </div>
                </div>
                <div className="manifest-details-slide-in__route-arrow">
                  <ArrowBlueRight />
                </div>
                <div className="manifest-details-slide-in__route-point">
                  <div className="manifest-details-slide-in__route-point-label">Destination</div>
                  <div className="manifest-details-slide-in__route-point-value">{destinationTerminal}</div>
                  <div className="manifest-details-slide-in__route-point-time">
                    Arr: {actualArrival}
                  </div>
                </div>
              </div>
              <div className="manifest-details-slide-in__route-footer">
                <div className="manifest-details-slide-in__field">
                  <div className="manifest-details-slide-in__field-label">Distance</div>
                  <div className="manifest-details-slide-in__field-value">{distance}</div>
                </div>
                <div className="manifest-details-slide-in__field">
                  <div className="manifest-details-slide-in__field-label">Destination Status</div>
                  <div className="manifest-details-slide-in__field-value">
                    {destinationStatus && destinationStatus !== "-" ? (
                      <Tag mods={{ color: getStatusColor(destinationStatus) }}>
                        {formatStatusLabel(destinationStatus)}
                      </Tag>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
              </div>
            </div>

          {/* Equipment & Driver */}
          <div className="manifest-details-slide-in__card">
            <div className="manifest-details-slide-in__card-header">Equipment & Driver</div>
            <div className="manifest-details-slide-in__field-row manifest-details-slide-in__field-row--gap">
              <div className="manifest-details-slide-in__field">
                <div className="manifest-details-slide-in__field-label">Tractor</div>
                <div className="manifest-details-slide-in__field-value">{tractorNumber}</div>
              </div>
              <div className="manifest-details-slide-in__field">
                <div className="manifest-details-slide-in__field-label">Trailer</div>
                <div className="manifest-details-slide-in__field-value">{trailerNumber}</div>
              </div>
              <div className="manifest-details-slide-in__field">
                <div className="manifest-details-slide-in__field-label">Team Drivers</div>
                <div className="manifest-details-slide-in__field-value">{teamDrivers}</div>
              </div>
            </div>
            <div className="manifest-details-slide-in__driver-section">
              <div className="manifest-details-slide-in__field manifest-details-slide-in__field--flex-1">
                <div className="manifest-details-slide-in__field-label">Driver</div>
                <div className="manifest-details-slide-in__field-value">
                  <div className="manifest-details-slide-in__driver-row">
                    <span>{driverName}</span>
                    {trackDriverUrl ? (
                      <a
                        href={trackDriverUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="manifest-details-slide-in__track-link"
                      >
                        Track Driver
                        <XGSIcon icon={XGSIcons.faExternalLinkAlt} size="sm" className="manifest-details-slide-in__track-link-icon" />
                      </a>
                    ) : (
                      <span className="manifest-details-slide-in__track-link manifest-details-slide-in__track-link--disabled">
                        Track Driver
                        <XGSIcon icon={XGSIcons.faExternalLinkAlt} size="sm" className="manifest-details-slide-in__track-link-icon" />
                      </span>
                    )}
                  </div>
                  {driverPhone !== "-" && (
                    <div className="manifest-details-slide-in__field-value manifest-details-slide-in__field-value--secondary">
                      {driverPhone}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Manifest Details */}
          <div className="manifest-details-slide-in__card">
            <div className="manifest-details-slide-in__card-header">Manifest Details</div>
            <div className="manifest-details-slide-in__manifest-details-content">
              <div className="manifest-details-slide-in__manifest-details-row">
                <div className="manifest-details-slide-in__field manifest-details-slide-in__field--carrier">
                  <div className="manifest-details-slide-in__field-label">Carrier</div>
                  <div className="manifest-details-slide-in__field-value">{carrier}</div>
                </div>
                <div className="manifest-details-slide-in__field">
                  <div className="manifest-details-slide-in__field-label">Division</div>
                  <div className="manifest-details-slide-in__field-value">{division}</div>
                </div>
                <div className="manifest-details-slide-in__field">
                  <div className="manifest-details-slide-in__field-label">Service Type</div>
                  <div className="manifest-details-slide-in__field-value">{serviceType}</div>
                </div>
              </div>
              <div className="manifest-details-slide-in__manifest-details-dispatched">
                <div className="manifest-details-slide-in__field">
                  <div className="manifest-details-slide-in__field-label">Dispatched Date</div>
                  <div className="manifest-details-slide-in__field-value">{dispatchedDate}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes - Full width */}
        <div className="manifest-details-slide-in__notes">
          <div className="manifest-details-slide-in__notes-card">
            <div className="manifest-details-slide-in__notes-header">Notes</div>
            <div className="manifest-details-slide-in__notes-field manifest-details-slide-in__notes-field--row">
              <label>Note Type</label>
              <div className="manifest-details-slide-in__notes-select-wrap">
                <XGSSelect
                  value={selectedNoteType}
                  onValueChange={(v) => setSelectedNoteType(v)}
                  placeholder="Select"
                  options={manifestNoteTypeOptions}
                />
              </div>
            </div>
            <div className="manifest-details-slide-in__notes-field manifest-details-slide-in__notes-field--stack">
              <textarea
                className="manifest-details-slide-in__notes-textarea"
                placeholder="Enter your note here..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={4}
              />
              <div className="manifest-details-slide-in__notes-actions">
                <Button
                  theme={ButtonThemes.blue}
                  size="medium"
                  onClick={handleAddNote}
                  disabled={!noteText.trim() || !selectedNoteType?.value || addNoteLoading}
                  spinner={addNoteLoading}
                >
                  Add Note
                </Button>
              </div>
            </div>
          </div>

          <div className="manifest-details-slide-in__recent-notes">
            <div className="manifest-details-slide-in__recent-notes-header">Recent Notes</div>
            {notes.length === 0 ? (
              <div className="manifest-details-slide-in__note-item-content" style={{ padding: "16px 0", color: "#4F5F72B8" }}>
                No notes yet.
              </div>
            ) : (
              notes.map((note, index) => (
                <div key={note.id ?? `note-${index}`} className="manifest-details-slide-in__note-item">
                  <div className="manifest-details-slide-in__note-item-header">
                    <div className="manifest-details-slide-in__note-item-header-left">
                      <Tag mods={{ color: TagColor.GREY }}>{note.noteType}</Tag>
                      <span className="manifest-details-slide-in__note-item-meta">
                        <span className="manifest-details-slide-in__note-item-driver">{note.updatedBy ?? note.createdBy ?? authorName}</span>
                        <span className="manifest-details-slide-in__note-item-date"> · {formatNoteDate(note.dateUpdated ?? note.dateCreated)}</span>
                      </span>
                    </div>
                    <div className="manifest-details-slide-in__note-item-actions">
                      <button
                        type="button"
                        className="manifest-details-slide-in__note-action manifest-details-slide-in__note-action--edit"
                        onClick={() => startEditNote(note, index)}
                        disabled={editingNoteIndex !== null || note.id == null}
                        title={note.id == null ? "Cannot edit notes from external systems" : "Edit"}
                        aria-label="Edit"
                      >
                        <XGSIcon icon={XGSIcons.faEdit} size="sm" />
                        <span>Edit</span>
                      </button>
                      <button
                        type="button"
                        className="manifest-details-slide-in__note-action manifest-details-slide-in__note-action--delete"
                        onClick={() => setDeleteModalNoteId(note.id ?? null)}
                        disabled={note.id == null}
                        title={note.id == null ? "Cannot delete notes from external systems" : "Delete"}
                        aria-label="Delete"
                      >
                        <XGSIcon icon={XGSIcons.faTrash} size="sm" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                  {editingNoteIndex === index ? (
                    <div className="manifest-details-slide-in__note-item-content" style={{ paddingTop: 12 }}>
                      <textarea
                        className="manifest-details-slide-in__notes-textarea"
                        value={editingNoteText}
                        onChange={(e) => setEditingNoteText(e.target.value)}
                        rows={3}
                        style={{ marginBottom: 12 }}
                      />
                      <div className="manifest-details-slide-in__notes-actions">
                        <Button
                          theme={ButtonThemes.gray}
                          size="medium"
                          onClick={() => {
                            setEditingNoteIndex(null);
                            setEditingNoteText("");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          theme={ButtonThemes.blue}
                          size="medium"
                          onClick={() => handleUpdateNote(note)}
                          disabled={!editingNoteText.trim() || updateNoteLoading}
                          spinner={updateNoteLoading}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="manifest-details-slide-in__note-item-content">{note.note}</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Financial Information - Full width bottom */}
        <div className="manifest-details-slide-in__financial">
          <div className="manifest-details-slide-in__financial-card">
            <div className="manifest-details-slide-in__financial-header">Financial Information</div>
            <div className="manifest-details-slide-in__financial-content">
              <div className="manifest-details-slide-in__financial-field">
                <div className="manifest-details-slide-in__financial-field-label">Total Cost</div>
                <div className="manifest-details-slide-in__financial-field-value">{totalCost}</div>
              </div>
              <div className="manifest-details-slide-in__financial-divider" />
              <div className="manifest-details-slide-in__financial-field">
                <div className="manifest-details-slide-in__financial-field-label">Rate Type</div>
                <div className="manifest-details-slide-in__financial-field-value">{rateType}</div>
              </div>
              <div className="manifest-details-slide-in__financial-field">
                <div className="manifest-details-slide-in__financial-field-label">Revenue</div>
                <div className="manifest-details-slide-in__financial-field-value">{revenue}</div>
              </div>
              <div className="manifest-details-slide-in__financial-field">
                <div className="manifest-details-slide-in__financial-field-label">Rate Override</div>
                <div className="manifest-details-slide-in__financial-field-value">{rateOverrideDisplay}</div>
              </div>
              <div className="manifest-details-slide-in__financial-field">
                <div className="manifest-details-slide-in__financial-field-label">Revenue %</div>
                <div className="manifest-details-slide-in__financial-field-value">{revenuePercent}</div>
              </div>
            </div>
          </div>
        </div>

        <ConfirmationModal
          opened={deleteModalNoteId != null}
          onCancel={() => setDeleteModalNoteId(null)}
          onConfirm={handleDeleteNote}
          header="Delete Note"
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
          spinner={deleteNoteLoading}
        >
          Are you sure you want to delete this note?
        </ConfirmationModal>
          </>
        )}
      </div>
    </SlideOutSidebar>
  );
};

export default ManifestDetailsSlideIn;
